import React from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack,
  IconButton,
  Tooltip,
  Container,
  TextField,
  InputAdornment,
  Button,
  Fade,
  Collapse,
  alpha,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Avatar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Refresh as RefreshIcon,
  SportsEsports as SportsEsportsIcon,
  Casino as CasinoIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  EmojiEvents as EmojiEventsIcon,
  FilterList as FilterListIcon,
  AccountBalance as AccountBalanceIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  SportsSoccer as SportsSoccerIcon,
  Store as StoreIcon,
  Computer as ComputerIcon,
  Stars as StarsIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import { useGetRecentBetsQuery, useLazyGetRecentBetsQuery } from '../../store/api/dashboardApi';
import { formatCurrency } from '../../utils/formatters';

const Games = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const tableTextColor = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.94)
    : theme.palette.text.primary;
  const tableMutedTextColor = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.82)
    : theme.palette.text.secondary;
  const tableHeaderColor = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.96)
    : theme.palette.text.primary;
  
  // Search and filter state
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchMode, setSearchMode] = React.useState(false);
  const [serviceCodeFilter, setServiceCodeFilter] = React.useState('');
  const [platformFilter, setPlatformFilter] = React.useState('');
  const [sideFilter, setSideFilter] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  
  // Get filters from Redux (including date range)
  const dashboardFilters = useSelector((state) => state.dashboard.filters);
  
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: isMobile ? 10 : 25,
  });

  // Main query - skipped during search mode, uses dashboard filters including date range
  const { 
    data: betsResponse, 
    isLoading, 
    error,
    refetch,
    isFetching 
  } = useGetRecentBetsQuery(dashboardFilters, {
    skip: searchMode,
  });

  // Lazy search query
  const [
    searchBets, 
    { 
      data: searchResults, 
      isLoading: isSearching, 
      error: searchError 
    }
  ] = useLazyGetRecentBetsQuery();

  React.useEffect(() => {
    setPaginationModel(prev => ({
      ...prev,
      pageSize: isMobile ? 10 : 25
    }));
  }, [isMobile]);

  // Handle search input change with debounce
  const searchTimeoutRef = React.useRef();

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      const trimmedQuery = value.trim();
      if (trimmedQuery) {
        performSearch(trimmedQuery);
      } else {
        clearSearch();
      }
    }, 500); // 500ms debounce
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'serviceCode':
        setServiceCodeFilter(value);
        break;
      case 'platform':
        setPlatformFilter(value);
        break;
      case 'side':
        setSideFilter(value);
        break;
      default:
        break;
    }
    
    if (searchInput.trim() || value) {
      performSearch(searchInput.trim());
    }
  };

  const performSearch = (query) => {
    const searchParams = {};
    
    if (query) {
      searchParams.search = query;
    }
    
    if (serviceCodeFilter) {
      searchParams.service_code = serviceCodeFilter;
    }
    
    // Add other filters if your API supports them
    if (platformFilter) {
      searchParams.platform = platformFilter;
    }
    
    if (sideFilter) {
      searchParams.side = sideFilter;
    }
    
    setSearchQuery(query);
    setSearchMode(true);
    searchBets(searchParams);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setServiceCodeFilter('');
    setPlatformFilter('');
    setSideFilter('');
    setSearchMode(false);
    setShowFilters(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Get display data based on search mode - memoized to prevent unnecessary recalculations
  const displayData = React.useMemo(() => {
    if (searchMode) {
      return searchResults || {
        data: [],
        pagination: { total: 0, currentPage: 1, totalPages: 1 },
        filters: {}
      };
    }
    return betsResponse;
  }, [searchMode, searchResults, betsResponse]);

  const bets = React.useMemo(() => displayData?.data || [], [displayData]);
  const pagination = displayData?.pagination || {};

  const rows = React.useMemo(() => {
    return bets.map((bet, index) => ({
      id: bet.gameRoundId || bet.sessionId || `bet-${index}`,
      ...bet,
      createdAt: bet.createdAt ? new Date(bet.createdAt) : null,
      updatedAt: bet.updatedAt ? new Date(bet.updatedAt) : null,
      // Calculate net result using the new field names
      netResult: (bet.amount_won || 0) - (bet.amount_lost || 0),
    }));
  }, [bets]);

  // Game-specific helper functions
  const getGameIcon = (serviceCode) => {
    const code = serviceCode?.toLowerCase();
    if (code?.includes('sport') || code?.includes('soccer') || code?.includes('football')) {
      return <SportsSoccerIcon sx={{ fontSize: 16 }} />;
    }
    if (code?.includes('casino') || code?.includes('slot')) {
      return <CasinoIcon sx={{ fontSize: 16 }} />;
    }
    return <SportsEsportsIcon sx={{ fontSize: 16 }} />;
  };

  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower?.includes('mobile') || platformLower?.includes('app')) {
      return <PhoneIcon sx={{ fontSize: 16 }} />;
    }
    if (platformLower?.includes('web') || platformLower?.includes('browser')) {
      return <ComputerIcon sx={{ fontSize: 16 }} />;
    }
    if (platformLower?.includes('station') || platformLower?.includes('kiosk')) {
      return <StoreIcon sx={{ fontSize: 16 }} />;
    }
    return <ComputerIcon sx={{ fontSize: 16 }} />;
  };

  const getGameColor = (serviceCode) => {
    const code = serviceCode?.toLowerCase();
    if (code?.includes('sport')) return 'success';
    if (code?.includes('casino')) return 'error';
    if (code?.includes('lottery')) return 'warning';
    return 'primary';
  };

  const getResultColor = (result) => {
    const resultLower = result?.toLowerCase();
    if (resultLower === 'win' || resultLower === 'won') return 'success';
    if (resultLower === 'loss' || resultLower === 'lost' || resultLower === 'lose') return 'error';
    if (resultLower === 'pending' || resultLower === 'active') return 'warning';
    return 'default';
  };

  const getResultIcon = (result) => {
    const resultLower = result?.toLowerCase();
    if (resultLower === 'win' || resultLower === 'won') return <CheckCircleIcon sx={{ fontSize: 16 }} />;
    if (resultLower === 'loss' || resultLower === 'lost' || resultLower === 'lose') return <ErrorIcon sx={{ fontSize: 16 }} />;
    if (resultLower === 'pending' || resultLower === 'active') return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
    return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
  };

  const getSideColor = (side) => {
    const sideLower = side?.toLowerCase();
    if (sideLower === 'heads' || sideLower === 'head') return 'primary';
    if (sideLower === 'tails' || sideLower === 'tail') return 'secondary';
    if (sideLower === 'over') return 'success';
    if (sideLower === 'under') return 'warning';
    return 'info';
  };

  const getColumns = React.useCallback(() => {
    const baseColumns = [
      { 
        field: 'phone', 
        headerName: 'Phone', 
        width: 140,
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: searchMode ? 700 : 600,
                color: searchMode ? 'primary.main' : tableTextColor
              }}
            >
              {params.value}
            </Typography>
          </Box>
        )
      },
      { 
        field: 'amount', 
        headerName: 'Bet Amount', 
        width: isMobile ? 100 : 120, 
        type: 'number',
        minWidth: 90,
        valueFormatter: (value) => formatCurrency(value ?? 0),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccountBalanceIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <Typography 
              variant="body2" 
              color="info.main"
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              {formatCurrency(params.value ?? 0)}
            </Typography>
          </Box>
        )
      },
      { 
        field: 'amount_won', 
        headerName: 'Won', 
        width: isMobile ? 80 : 100, 
        type: 'number',
        minWidth: 70,
        valueFormatter: (value) => formatCurrency(value ?? 0),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography 
              variant="body2" 
              color="success.main"
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              {formatCurrency(params.value ?? 0)}
            </Typography>
          </Box>
        )
      },
      { 
        field: 'amount_lost', 
        headerName: 'Lost', 
        width: isMobile ? 80 : 100, 
        type: 'number',
        minWidth: 70,
        valueFormatter: (value) => formatCurrency(value ?? 0),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
            <Typography 
              variant="body2" 
              color="error.main"
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              {formatCurrency(params.value ?? 0)}
            </Typography>
          </Box>
        )
      },
      { 
        field: 'side', 
        headerName: 'Side', 
        width: isMobile ? 80 : 100, 
        minWidth: 70,
        renderCell: (params) => (
          <Chip 
            label={params.value || 'N/A'}
            color={getSideColor(params.value)}
            size="small"
            variant="outlined"
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          />
        )
      }
    ];

    if (!isMobile) {
      baseColumns.push(
        { 
          field: 'result', 
          headerName: 'Result', 
          width: 100,
          renderCell: (params) => (
            <Chip 
              label={params.value || 'PENDING'}
              color={getResultColor(params.value)}
              size="small"
              variant="outlined"
              icon={getResultIcon(params.value)}
              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
            />
          )
        },
        { 
          field: 'points', 
          headerName: 'Points', 
          width: 80, 
          type: 'number',
          valueFormatter: (value) => value?.toFixed(1) || '0.0'
        }
      );
    }

    if (!isTablet) {
      baseColumns.push(
        { 
          field: 'service_code', 
          headerName: 'Service', 
          width: 120,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getGameIcon(params.value)}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: tableTextColor,
                }}
              >
                {params.value || 'N/A'}
              </Typography>
            </Box>
          )
        },
        { 
          field: 'platform', 
          headerName: 'Platform', 
          width: 120,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getPlatformIcon(params.value)}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: tableTextColor,
                }}
              >
                {params.value || 'N/A'}
              </Typography>
            </Box>
          )
        },
        { 
          field: 'station_name', 
          headerName: 'Station', 
          width: 120,
          renderCell: (params) => (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.8rem',
                fontWeight: 600,
                color: tableMutedTextColor
              }}
            >
              {params.value || 'N/A'}
            </Typography>
          )
        }
      );
    }

    return baseColumns;
  }, [isMobile, isTablet, searchMode]);

  // Get unique values for filters
  const filterOptions = React.useMemo(() => {
    const serviceCodes = new Set();
    const platforms = new Set();
    const sides = new Set();
    
    bets.forEach(bet => {
      if (bet.serviceCode) serviceCodes.add(bet.serviceCode);
      if (bet.platform) platforms.add(bet.platform);
      if (bet.side) sides.add(bet.side);
    });
    
    return {
      serviceCodes: Array.from(serviceCodes).sort(),
      platforms: Array.from(platforms).sort(),
      sides: Array.from(sides).sort()
    };
  }, [bets]);

  // Calculate comprehensive game statistics
  const gameStats = React.useMemo(() => {
    const stats = {
      totalBets: bets.length,
      totalBetAmount: 0,
      totalWon: 0,
      totalLost: 0,
      uniquePlayers: new Set(),
      gameBreakdown: {},
      platformBreakdown: {},
      sideBreakdown: {},
      resultBreakdown: {},
      avgPoints: 0,
      totalPoints: 0
    };

    bets.forEach(bet => {
      stats.totalBetAmount += bet.amount || 0;
      // Use the new field names
      stats.totalWon += bet.amount_won || 0;
      stats.totalLost += bet.amount_lost || 0;
      stats.totalPoints += bet.points || 0;
      
      if (bet.phone) stats.uniquePlayers.add(bet.phone);
      
      const game = bet.serviceCode || 'Unknown';
      stats.gameBreakdown[game] = (stats.gameBreakdown[game] || 0) + 1;
      
      const platform = bet.platform || 'Unknown';
      stats.platformBreakdown[platform] = (stats.platformBreakdown[platform] || 0) + 1;
      
      const side = bet.side || 'Unknown';
      stats.sideBreakdown[side] = (stats.sideBreakdown[side] || 0) + 1;
      
      const result = bet.result || 'PENDING';
      stats.resultBreakdown[result] = (stats.resultBreakdown[result] || 0) + 1;
    });

    stats.uniquePlayers = stats.uniquePlayers.size;
    stats.avgBetAmount = stats.totalBets > 0 ? stats.totalBetAmount / stats.totalBets : 0;
    stats.avgPoints = stats.totalBets > 0 ? stats.totalPoints / stats.totalBets : 0;
    stats.winRate = stats.totalBets > 0 ? (Object.entries(stats.resultBreakdown).filter(([result]) => 
      result.toLowerCase().includes('win')).reduce((sum, [, count]) => sum + count, 0) / stats.totalBets * 100) : 0;

    return stats;
  }, [bets]);

  // Updated summary cards without "Net House Profit"
  const summaryCards = [
    { 
      title: 'Total Bets', 
      value: gameStats.totalBets.toLocaleString(), 
      color: 'primary.main', 
      icon: <SportsEsportsIcon />, 
      bgColor: theme.palette.primary.light 
    },
    { 
      title: 'Total Bet Amount', 
      value: formatCurrency(gameStats.totalBetAmount), 
      color: 'info.main', 
      icon: <AccountBalanceIcon />, 
      bgColor: theme.palette.info.light 
    },
    { 
      title: 'Total Won', 
      value: formatCurrency(gameStats.totalWon), 
      color: 'success.main', 
      icon: <TrendingUpIcon />, 
      bgColor: theme.palette.success.light 
    },
    { 
      title: 'Active Players', 
      value: gameStats.uniquePlayers.toLocaleString(), 
      color: 'warning.main', 
      icon: <PeopleIcon />, 
      bgColor: theme.palette.warning.light 
    }
  ];

  const LoadingSkeleton = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
    </Box>
  );

  if (isLoading && !searchMode) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
          <LoadingSkeleton />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 2 }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3 
        }}>
          <Box>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'}
              component="h1" 
              sx={{ fontWeight: 'bold', mb: 1 }}
            >
              Games & Bets {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchMode ? `Search results for: ${searchQuery || 'filtered data'}` : 'Recent betting activity and game performance analysis'}
            </Typography>
          </Box>
          
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={() => refetch()} 
              disabled={isFetching || isSearching} 
              color="primary" 
              sx={{ 
                bgcolor: 'primary.light', 
                '&:hover': { bgcolor: 'primary.main', color: 'white' } 
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%', 
                background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
                border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
                transition: 'transform 0.2s, box-shadow 0.2s', 
                '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
              }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                      {card.icon}
                    </Box>
                    <Typography 
                      component="div" 
                      sx={{ 
                        color: card.color, 
                        fontWeight: 'bold', 
                        fontSize: { xs: '1rem', md: '1.25rem' } 
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Stack>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                  >
                    {card.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Game Analytics Dashboard */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Platform Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ComputerIcon />
                  Platform Distribution
                </Typography>
                {Object.entries(gameStats.platformBreakdown).map(([platform, count]) => {
                  const percentage = (count / gameStats.totalBets) * 100;
                  return (
                    <Box key={platform} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getPlatformIcon(platform)}
                          <Typography variant="body2">{platform}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {count} ({percentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={percentage} 
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          {/* Side/Result Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnalyticsIcon />
                  Betting Sides
                </Typography>
                {Object.entries(gameStats.sideBreakdown).map(([side, count]) => {
                  const percentage = (count / gameStats.totalBets) * 100;
                  return (
                    <Box key={side} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Chip 
                          label={side}
                          color={getSideColor(side)}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {count} ({percentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={percentage} 
                        color={getSideColor(side)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter Section */}
        <Card sx={{ 
          mb: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'transform 0.2s, box-shadow 0.2s', 
          '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {/* Search Bar */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by phone, game round ID, or session..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton onClick={clearSearch} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                    }
                  }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ minWidth: 'fit-content' }}
                >
                  Filters
                </Button>
                
                {isSearching && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Searching...</Typography>
                  </Box>
                )}
              </Stack>
              
              {/* Filters */}
              <Collapse in={showFilters}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pt: 2 }}>
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Service</InputLabel>
                    <Select
                      value={serviceCodeFilter}
                      label="Service"
                      onChange={(e) => handleFilterChange('serviceCode', e.target.value)}
                    >
                      <MenuItem value="">All Services</MenuItem>
                      {filterOptions.serviceCodes.map((code) => (
                        <MenuItem key={code} value={code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getGameIcon(code)}
                            {code}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Platform</InputLabel>
                    <Select
                      value={platformFilter}
                      label="Platform"
                      onChange={(e) => handleFilterChange('platform', e.target.value)}
                    >
                      <MenuItem value="">All Platforms</MenuItem>
                      {filterOptions.platforms.map((platform) => (
                        <MenuItem key={platform} value={platform}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getPlatformIcon(platform)}
                            {platform}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Side</InputLabel>
                    <Select
                      value={sideFilter}
                      label="Side"
                      onChange={(e) => handleFilterChange('side', e.target.value)}
                    >
                      <MenuItem value="">All Sides</MenuItem>
                      {filterOptions.sides.map((side) => (
                        <MenuItem key={side} value={side}>
                          <Chip 
                            label={side}
                            color={getSideColor(side)}
                            size="small"
                            variant="outlined"
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Collapse>
            </Stack>
          </CardContent>
        </Card>

        {/* Search Results Alert */}
        {searchMode && bets.length === 0 && (
          <Fade in>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                No bets found for the current search criteria.
              </Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
                View All Bets
              </Button>
            </Alert>
          </Fade>
        )}

        {searchMode && bets.length > 0 && (
          <Fade in>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                ✅ Found {bets.length} bet{bets.length !== 1 ? 's' : ''}
              </Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
                View All Bets
              </Button>
            </Alert>
          </Fade>
        )}

        {/* Error Handling */}
        {(error || searchError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading bet data: {error?.message || searchError?.message || 'Unknown error'}
          </Alert>
        )}

        {/* Additional Stats Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary.main">
                {formatCurrency(gameStats.avgBetAmount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Average Bet
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {gameStats.avgPoints.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Average Points
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">
                {gameStats.winRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Player Win Rate
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">
                {formatCurrency(gameStats.totalLost)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Lost
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Data Grid Section */}
        <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={getColumns()}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
            rowCount={pagination.total || rows.length}
            loading={isLoading || isFetching || isSearching}
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              color: tableTextColor,
              '& .MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderBottomColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.9),
                color: tableTextColor,
                fontWeight: 500,
              },
              '& .MuiDataGrid-columnHeaders': { 
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha(theme.palette.primary.light, 0.18),
                borderBottom: '2px solid', 
                borderBottomColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.2),
              },
              '& .MuiDataGrid-columnHeader': {
                color: tableHeaderColor,
                fontWeight: 700,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: tableHeaderColor,
                fontWeight: 800,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.primary.main, 0.06),
              },
              '& .MuiDataGrid-row:nth-of-type(even)': { 
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.action.hover, 0.65),
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.015)
                  : theme.palette.background.paper,
              },
              '& .MuiDataGrid-footerContainer, & .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                color: tableMutedTextColor,
              },
              ...(isMobile && {
                '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 700 },
                '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
              }),
            }}
            getRowId={(row) => row.id}
            density={isMobile ? 'compact' : 'standard'}
          />
        </Paper>

        {/* Pagination Info */}
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: 1 
        }}>
          <Typography variant="caption" color="text.secondary">
            {searchMode && <Chip label="Filtered" size="small" sx={{ mr: 1 }} />}
            Page {pagination.currentPage || 1} of {pagination.totalPages || 1} • 
            Total Won: {formatCurrency(gameStats.totalWon)} • Total Lost: {formatCurrency(gameStats.totalLost)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {rows.length} bet{rows.length !== 1 ? 's' : ''} of {pagination.total || rows.length} total
            {searchMode ? ` (Search Results)` : ' (Recent Bets)'}
          </Typography>
        </Box>

        {/* No bets found */}
        {!searchMode && !isLoading && bets.length === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            No betting data available. This could be due to no recent activity or database connectivity issues.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default Games;

// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container,
//   TextField,
//   InputAdornment,
//   Button,
//   Fade,
//   Collapse,
//   alpha,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Select,
//   LinearProgress,
//   Avatar
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   SportsEsports as SportsEsportsIcon,
//   Casino as CasinoIcon,
//   People as PeopleIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
//   Search as SearchIcon,
//   Clear as ClearIcon,
//   Phone as PhoneIcon,
//   EmojiEvents as EmojiEventsIcon,
//   FilterList as FilterListIcon,
//   AccountBalance as AccountBalanceIcon,
//   Analytics as AnalyticsIcon,
//   CheckCircle as CheckCircleIcon,
//   Error as ErrorIcon,
//   HourglassEmpty as HourglassEmptyIcon,
//   SportsSoccer as SportsSoccerIcon,
//   Store as StoreIcon,
//   Computer as ComputerIcon,
//   Stars as StarsIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetRecentBetsQuery, useLazyGetRecentBetsQuery } from '../../store/api/dashboardApi';

// const Games = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   // Search and filter state
//   const [searchInput, setSearchInput] = React.useState('');
//   const [searchQuery, setSearchQuery] = React.useState('');
//   const [searchMode, setSearchMode] = React.useState(false);
//   const [serviceCodeFilter, setServiceCodeFilter] = React.useState('');
//   const [platformFilter, setPlatformFilter] = React.useState('');
//   const [sideFilter, setSideFilter] = React.useState('');
//   const [showFilters, setShowFilters] = React.useState(false);
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   // Main query - skipped during search mode
//   const { 
//     data: betsResponse, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetRecentBetsQuery(allTimeFilters, {
//     skip: searchMode,
//   });

//   // Lazy search query
//   const [
//     searchBets, 
//     { 
//       data: searchResults, 
//       isLoading: isSearching, 
//       error: searchError 
//     }
//   ] = useLazyGetRecentBetsQuery();

//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Handle search input change with debounce
//   const searchTimeoutRef = React.useRef();

//   const handleSearchInputChange = (event) => {
//     const value = event.target.value;
//     setSearchInput(value);
    
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
    
//     searchTimeoutRef.current = setTimeout(() => {
//       const trimmedQuery = value.trim();
//       if (trimmedQuery) {
//         performSearch(trimmedQuery);
//       } else {
//         clearSearch();
//       }
//     }, 500); // 500ms debounce
//   };

//   const handleFilterChange = (filterType, value) => {
//     switch (filterType) {
//       case 'serviceCode':
//         setServiceCodeFilter(value);
//         break;
//       case 'platform':
//         setPlatformFilter(value);
//         break;
//       case 'side':
//         setSideFilter(value);
//         break;
//     }
    
//     if (searchInput.trim() || value) {
//       performSearch(searchInput.trim());
//     }
//   };

//   const performSearch = (query) => {
//     const searchParams = {};
    
//     if (query) {
//       searchParams.search = query;
//     }
    
//     if (serviceCodeFilter) {
//       searchParams.service_code = serviceCodeFilter;
//     }
    
//     // Add other filters if your API supports them
//     if (platformFilter) {
//       searchParams.platform = platformFilter;
//     }
    
//     if (sideFilter) {
//       searchParams.side = sideFilter;
//     }
    
//     setSearchQuery(query);
//     setSearchMode(true);
//     searchBets(searchParams);
//   };

//   const clearSearch = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setServiceCodeFilter('');
//     setPlatformFilter('');
//     setSideFilter('');
//     setSearchMode(false);
//     setShowFilters(false);
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
//   };

//   // Get display data based on search mode
//   const getDisplayData = () => {
//     if (searchMode) {
//       return searchResults || {
//         data: [],
//         pagination: { total: 0, currentPage: 1, totalPages: 1 },
//         filters: {}
//       };
//     }
//     return betsResponse;
//   };

//   const displayData = getDisplayData();
//   const bets = displayData?.data || [];
//   const pagination = displayData?.pagination || {};

//   const rows = React.useMemo(() => {
//     return bets.map((bet, index) => ({
//       id: bet.gameRoundId || bet.sessionId || `bet-${index}`,
//       ...bet,
//       createdAt: bet.createdAt ? new Date(bet.createdAt) : null,
//       updatedAt: bet.updatedAt ? new Date(bet.updatedAt) : null,
//       // Calculate net result
//       netResult: (bet.amountWon || 0) - (bet.amountLost || 0),
//     }));
//   }, [bets]);

//   // Game-specific helper functions
//   const getGameIcon = (serviceCode) => {
//     const code = serviceCode?.toLowerCase();
//     if (code?.includes('sport') || code?.includes('soccer') || code?.includes('football')) {
//       return <SportsSoccerIcon sx={{ fontSize: 16 }} />;
//     }
//     if (code?.includes('casino') || code?.includes('slot')) {
//       return <CasinoIcon sx={{ fontSize: 16 }} />;
//     }
//     return <SportsEsportsIcon sx={{ fontSize: 16 }} />;
//   };

//   const getPlatformIcon = (platform) => {
//     const platformLower = platform?.toLowerCase();
//     if (platformLower?.includes('mobile') || platformLower?.includes('app')) {
//       return <PhoneIcon sx={{ fontSize: 16 }} />;
//     }
//     if (platformLower?.includes('web') || platformLower?.includes('browser')) {
//       return <ComputerIcon sx={{ fontSize: 16 }} />;
//     }
//     if (platformLower?.includes('station') || platformLower?.includes('kiosk')) {
//       return <StoreIcon sx={{ fontSize: 16 }} />;
//     }
//     return <ComputerIcon sx={{ fontSize: 16 }} />;
//   };

//   const getGameColor = (serviceCode) => {
//     const code = serviceCode?.toLowerCase();
//     if (code?.includes('sport')) return 'success';
//     if (code?.includes('casino')) return 'error';
//     if (code?.includes('lottery')) return 'warning';
//     return 'primary';
//   };

//   const getResultColor = (result) => {
//     const resultLower = result?.toLowerCase();
//     if (resultLower === 'win' || resultLower === 'won') return 'success';
//     if (resultLower === 'loss' || resultLower === 'lost' || resultLower === 'lose') return 'error';
//     if (resultLower === 'pending' || resultLower === 'active') return 'warning';
//     return 'default';
//   };

//   const getResultIcon = (result) => {
//     const resultLower = result?.toLowerCase();
//     if (resultLower === 'win' || resultLower === 'won') return <CheckCircleIcon sx={{ fontSize: 16 }} />;
//     if (resultLower === 'loss' || resultLower === 'lost' || resultLower === 'lose') return <ErrorIcon sx={{ fontSize: 16 }} />;
//     if (resultLower === 'pending' || resultLower === 'active') return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
//     return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
//   };

//   const getSideColor = (side) => {
//     const sideLower = side?.toLowerCase();
//     if (sideLower === 'heads' || sideLower === 'head') return 'primary';
//     if (sideLower === 'tails' || sideLower === 'tail') return 'secondary';
//     if (sideLower === 'over') return 'success';
//     if (sideLower === 'under') return 'warning';
//     return 'info';
//   };

//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' },
//                 fontWeight: searchMode ? 'bold' : 'normal',
//                 color: searchMode ? 'primary.main' : 'inherit'
//               }}
//             >
//               {params.value}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'amount', 
//         headerName: 'Bet Amount', 
//         width: isMobile ? 100 : 120, 
//         type: 'number',
//         minWidth: 90,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             <AccountBalanceIcon sx={{ fontSize: 16, color: 'info.main' }} />
//             <Typography 
//               variant="body2" 
//               color="info.main"
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'netResult', 
//         headerName: isMobile ? 'Net' : 'Net Result', 
//         width: isMobile ? 90 : 120, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => {
//           const isPositive = params.value > 0;
//           return (
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//               {isPositive ? (
//                 <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
//               ) : (
//                 <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
//               )}
//               <Typography 
//                 variant="body2" 
//                 color={isPositive ? 'success.main' : 'error.main'}
//                 sx={{ 
//                   fontWeight: 'medium',
//                   fontSize: { xs: '0.75rem', md: '0.875rem' }
//                 }}
//               >
//                 ${Math.abs(params.value || 0).toFixed(2)}
//               </Typography>
//             </Box>
//           );
//         }
//       },
//       { 
//         field: 'side', 
//         headerName: 'Side', 
//         width: isMobile ? 80 : 100, 
//         minWidth: 70,
//         renderCell: (params) => (
//           <Chip 
//             label={params.value || 'N/A'}
//             color={getSideColor(params.value)}
//             size="small"
//             variant="outlined"
//             sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//           />
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { 
//           field: 'result', 
//           headerName: 'Result', 
//           width: 100,
//           renderCell: (params) => (
//             <Chip 
//               label={params.value || 'PENDING'}
//               color={getResultColor(params.value)}
//               size="small"
//               variant="outlined"
//               icon={getResultIcon(params.value)}
//               sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//             />
//           )
//         },
//         { 
//           field: 'points', 
//           headerName: 'Points', 
//           width: 80, 
//           type: 'number',
//           valueFormatter: (value) => value?.toFixed(1) || '0.0'
//         }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { 
//           field: 'service_code', 
//           headerName: 'Service', 
//           width: 120,
//           renderCell: (params) => (
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//               {getGameIcon(params.value)}
//               <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
//                 {params.value || 'N/A'}
//               </Typography>
//             </Box>
//           )
//         },
//         { 
//           field: 'platform', 
//           headerName: 'Platform', 
//           width: 120,
//           renderCell: (params) => (
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//               {getPlatformIcon(params.value)}
//               <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
//                 {params.value || 'N/A'}
//               </Typography>
//             </Box>
//           )
//         },
//         { 
//           field: 'station_name', 
//           headerName: 'Station', 
//           width: 120,
//           renderCell: (params) => (
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontSize: '0.8rem',
//                 color: 'text.secondary'
//               }}
//             >
//               {params.value || 'N/A'}
//             </Typography>
//           )
//         }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet, searchMode]);

//   // Get unique values for filters
//   const filterOptions = React.useMemo(() => {
//     const serviceCodes = new Set();
//     const platforms = new Set();
//     const sides = new Set();
    
//     bets.forEach(bet => {
//       if (bet.serviceCode) serviceCodes.add(bet.serviceCode);
//       if (bet.platform) platforms.add(bet.platform);
//       if (bet.side) sides.add(bet.side);
//     });
    
//     return {
//       serviceCodes: Array.from(serviceCodes).sort(),
//       platforms: Array.from(platforms).sort(),
//       sides: Array.from(sides).sort()
//     };
//   }, [bets]);

//   // Calculate comprehensive game statistics
//   const gameStats = React.useMemo(() => {
//     const stats = {
//       totalBets: bets.length,
//       totalBetAmount: 0,
//       totalWon: 0,
//       totalLost: 0,
//       uniquePlayers: new Set(),
//       gameBreakdown: {},
//       platformBreakdown: {},
//       sideBreakdown: {},
//       resultBreakdown: {},
//       avgPoints: 0,
//       totalPoints: 0
//     };

//     bets.forEach(bet => {
//       stats.totalBetAmount += bet.amount || 0;
//       stats.totalWon += bet.amountWon || 0;
//       stats.totalLost += bet.amountLost || 0;
//       stats.totalPoints += bet.points || 0;
      
//       if (bet.phone) stats.uniquePlayers.add(bet.phone);
      
//       const game = bet.serviceCode || 'Unknown';
//       stats.gameBreakdown[game] = (stats.gameBreakdown[game] || 0) + 1;
      
//       const platform = bet.platform || 'Unknown';
//       stats.platformBreakdown[platform] = (stats.platformBreakdown[platform] || 0) + 1;
      
//       const side = bet.side || 'Unknown';
//       stats.sideBreakdown[side] = (stats.sideBreakdown[side] || 0) + 1;
      
//       const result = bet.result || 'PENDING';
//       stats.resultBreakdown[result] = (stats.resultBreakdown[result] || 0) + 1;
//     });

//     stats.uniquePlayers = stats.uniquePlayers.size;
//     stats.avgBetAmount = stats.totalBets > 0 ? stats.totalBetAmount / stats.totalBets : 0;
//     stats.avgPoints = stats.totalBets > 0 ? stats.totalPoints / stats.totalBets : 0;
//     stats.netProfit = stats.totalWon - stats.totalLost;
//     stats.winRate = stats.totalBets > 0 ? (Object.entries(stats.resultBreakdown).filter(([result]) => 
//       result.toLowerCase().includes('win')).reduce((sum, [, count]) => sum + count, 0) / stats.totalBets * 100) : 0;

//     return stats;
//   }, [bets]);

//   const summaryCards = [
//     { 
//       title: 'Total Bets', 
//       value: gameStats.totalBets.toLocaleString(), 
//       color: 'primary.main', 
//       icon: <SportsEsportsIcon />, 
//       bgColor: theme.palette.primary.light 
//     },
//     { 
//       title: 'Total Bet Amount', 
//       value: `$${gameStats.totalBetAmount.toLocaleString()}`, 
//       color: 'info.main', 
//       icon: <AccountBalanceIcon />, 
//       bgColor: theme.palette.info.light 
//     },
//     { 
//       title: 'Net House Profit', 
//       value: `$${Math.abs(gameStats.netProfit).toLocaleString()}`, 
//       color: gameStats.netProfit >= 0 ? 'success.main' : 'error.main', 
//       icon: gameStats.netProfit >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />, 
//       bgColor: gameStats.netProfit >= 0 ? theme.palette.success.light : theme.palette.error.light 
//     },
//     { 
//       title: 'Active Players', 
//       value: gameStats.uniquePlayers.toLocaleString(), 
//       color: 'warning.main', 
//       icon: <PeopleIcon />, 
//       bgColor: theme.palette.warning.light 
//     }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading && !searchMode) {
//     return (
//       <Container maxWidth="xl">
//         <Box sx={{ py: 2 }}>
//           <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
//           <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
//           <LoadingSkeleton />
//         </Box>
//       </Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'}
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Games & Bets {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               {searchMode ? `Search results for: ${searchQuery || 'filtered data'}` : 'Recent betting activity and game performance analysis'}
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton 
//               onClick={() => refetch()} 
//               disabled={isFetching || isSearching} 
//               color="primary" 
//               sx={{ 
//                 bgcolor: 'primary.light', 
//                 '&:hover': { bgcolor: 'primary.main', color: 'white' } 
//               }}
//             >
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Summary Cards */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ 
//                 height: '100%', 
//                 background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
//                 border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
//                 transition: 'transform 0.2s, box-shadow 0.2s', 
//                 '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
//               }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
//                       {card.icon}
//                     </Box>
//                     <Typography 
//                       component="div" 
//                       sx={{ 
//                         color: card.color, 
//                         fontWeight: 'bold', 
//                         fontSize: { xs: '1rem', md: '1.25rem' } 
//                       }}
//                     >
//                       {card.value}
//                     </Typography>
//                   </Stack>
//                   <Typography 
//                     variant="body2" 
//                     color="text.secondary" 
//                     sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
//                   >
//                     {card.title}
//                   </Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Game Analytics Dashboard */}
//         <Grid container spacing={3} sx={{ mb: 3 }}>
//           {/* Platform Breakdown */}
//           <Grid item xs={12} md={6}>
//             <Card>
//               <CardContent>
//                 <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <ComputerIcon />
//                   Platform Distribution
//                 </Typography>
//                 {Object.entries(gameStats.platformBreakdown).map(([platform, count]) => {
//                   const percentage = (count / gameStats.totalBets) * 100;
//                   return (
//                     <Box key={platform} sx={{ mb: 2 }}>
//                       <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                           {getPlatformIcon(platform)}
//                           <Typography variant="body2">{platform}</Typography>
//                         </Box>
//                         <Typography variant="body2" color="text.secondary">
//                           {count} ({percentage.toFixed(1)}%)
//                         </Typography>
//                       </Box>
//                       <LinearProgress 
//                         variant="determinate" 
//                         value={percentage} 
//                         sx={{ height: 6, borderRadius: 3 }}
//                       />
//                     </Box>
//                   );
//                 })}
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* Side/Result Breakdown */}
//           <Grid item xs={12} md={6}>
//             <Card>
//               <CardContent>
//                 <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <AnalyticsIcon />
//                   Betting Sides
//                 </Typography>
//                 {Object.entries(gameStats.sideBreakdown).map(([side, count]) => {
//                   const percentage = (count / gameStats.totalBets) * 100;
//                   return (
//                     <Box key={side} sx={{ mb: 2 }}>
//                       <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                         <Chip 
//                           label={side}
//                           color={getSideColor(side)}
//                           size="small"
//                           variant="outlined"
//                         />
//                         <Typography variant="body2" color="text.secondary">
//                           {count} ({percentage.toFixed(1)}%)
//                         </Typography>
//                       </Box>
//                       <LinearProgress 
//                         variant="determinate" 
//                         value={percentage} 
//                         color={getSideColor(side)}
//                         sx={{ height: 6, borderRadius: 3 }}
//                       />
//                     </Box>
//                   );
//                 })}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {/* Search and Filter Section */}
//         <Card sx={{ 
//           mb: 3, 
//           background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
//           border: '1px solid',
//           borderColor: 'divider',
//           transition: 'transform 0.2s, box-shadow 0.2s', 
//           '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
//         }}>
//           <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//             <Stack spacing={2}>
//               {/* Search Bar */}
//               <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
//                 <TextField
//                   fullWidth
//                   placeholder="Search by phone, game round ID, or session..."
//                   value={searchInput}
//                   onChange={handleSearchInputChange}
//                   InputProps={{
//                     startAdornment: (
//                       <InputAdornment position="start">
//                         <SearchIcon color="action" />
//                       </InputAdornment>
//                     ),
//                     endAdornment: searchInput && (
//                       <InputAdornment position="end">
//                         <IconButton onClick={clearSearch} size="small">
//                           <ClearIcon />
//                         </IconButton>
//                       </InputAdornment>
//                     )
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       backgroundColor: theme.palette.background.paper,
//                     }
//                   }}
//                 />
                
//                 <Button
//                   variant="outlined"
//                   startIcon={<FilterListIcon />}
//                   onClick={() => setShowFilters(!showFilters)}
//                   sx={{ minWidth: 'fit-content' }}
//                 >
//                   Filters
//                 </Button>
                
//                 {isSearching && (
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
//                     <CircularProgress size={20} />
//                     <Typography variant="body2" color="text.secondary">Searching...</Typography>
//                   </Box>
//                 )}
//               </Stack>
              
//               {/* Filters */}
//               <Collapse in={showFilters}>
//                 <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pt: 2 }}>
//                   <FormControl sx={{ minWidth: 150 }}>
//                     <InputLabel>Service</InputLabel>
//                     <Select
//                       value={serviceCodeFilter}
//                       label="Service"
//                       onChange={(e) => handleFilterChange('serviceCode', e.target.value)}
//                     >
//                       <MenuItem value="">All Services</MenuItem>
//                       {filterOptions.serviceCodes.map((code) => (
//                         <MenuItem key={code} value={code}>
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                             {getGameIcon(code)}
//                             {code}
//                           </Box>
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>

//                   <FormControl sx={{ minWidth: 150 }}>
//                     <InputLabel>Platform</InputLabel>
//                     <Select
//                       value={platformFilter}
//                       label="Platform"
//                       onChange={(e) => handleFilterChange('platform', e.target.value)}
//                     >
//                       <MenuItem value="">All Platforms</MenuItem>
//                       {filterOptions.platforms.map((platform) => (
//                         <MenuItem key={platform} value={platform}>
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                             {getPlatformIcon(platform)}
//                             {platform}
//                           </Box>
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>

//                   <FormControl sx={{ minWidth: 150 }}>
//                     <InputLabel>Side</InputLabel>
//                     <Select
//                       value={sideFilter}
//                       label="Side"
//                       onChange={(e) => handleFilterChange('side', e.target.value)}
//                     >
//                       <MenuItem value="">All Sides</MenuItem>
//                       {filterOptions.sides.map((side) => (
//                         <MenuItem key={side} value={side}>
//                           <Chip 
//                             label={side}
//                             color={getSideColor(side)}
//                             size="small"
//                             variant="outlined"
//                           />
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Stack>
//               </Collapse>
//             </Stack>
//           </CardContent>
//         </Card>

//         {/* Search Results Alert */}
//         {searchMode && bets.length === 0 && (
//           <Fade in>
//             <Alert severity="info" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 No bets found for the current search criteria.
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Bets
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {searchMode && bets.length > 0 && (
//           <Fade in>
//             <Alert severity="success" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 ✅ Found {bets.length} bet{bets.length !== 1 ? 's' : ''}
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Bets
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {/* Error Handling */}
//         {(error || searchError) && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             Error loading bet data: {error?.message || searchError?.message || 'Unknown error'}
//           </Alert>
//         )}

//         {/* Additional Stats Row */}
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={12} sm={3}>
//             <Paper sx={{ p: 2, textAlign: 'center' }}>
//               <Typography variant="h6" color="primary.main">
//                 ${gameStats.avgBetAmount.toFixed(2)}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Average Bet
//               </Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={12} sm={3}>
//             <Paper sx={{ p: 2, textAlign: 'center' }}>
//               <Typography variant="h6" color="success.main">
//                 {gameStats.avgPoints.toFixed(1)}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Average Points
//               </Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={12} sm={3}>
//             <Paper sx={{ p: 2, textAlign: 'center' }}>
//               <Typography variant="h6" color="warning.main">
//                 {gameStats.winRate.toFixed(1)}%
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Player Win Rate
//               </Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={12} sm={3}>
//             <Paper sx={{ p: 2, textAlign: 'center' }}>
//               <Typography variant="h6" color="info.main">
//                 {Object.keys(gameStats.gameBreakdown).length}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Game Types
//               </Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Data Grid Section */}
//         <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//           <DataGrid
//             rows={rows}
//             columns={getColumns()}
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//             rowCount={pagination.total || rows.length}
//             loading={isLoading || isFetching || isSearching}
//             disableRowSelectionOnClick
//             sx={{
//               '& .MuiDataGrid-root': { border: 'none' },
//               '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider' },
//               '& .MuiDataGrid-columnHeaders': { 
//                 backgroundColor: alpha(theme.palette.primary.light, 0.1), 
//                 borderBottom: '2px solid', 
//                 borderBottomColor: 'divider' 
//               },
//               '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//               '& .MuiDataGrid-row:nth-of-type(even)': { 
//                 backgroundColor: alpha(theme.palette.action.hover, 0.5) 
//               },
//               ...(isMobile && {
//                 '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                 '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//               }),
//             }}
//             getRowId={(row) => row.id}
//             density={isMobile ? 'compact' : 'standard'}
//           />
//         </Paper>

//         {/* Pagination Info */}
//         <Box sx={{ 
//           mt: 2, 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: 'center', 
//           flexDirection: { xs: 'column', md: 'row' }, 
//           gap: 1 
//         }}>
//           <Typography variant="caption" color="text.secondary">
//             {searchMode && <Chip label="Filtered" size="small" sx={{ mr: 1 }} />}
//             Page {pagination.currentPage || 1} of {pagination.totalPages || 1} • 
//             House Profit: ${gameStats.netProfit.toLocaleString()}
//           </Typography>
//           <Typography variant="caption" color="text.secondary">
//             Showing {rows.length} bet{rows.length !== 1 ? 's' : ''} of {pagination.total || rows.length} total
//             {searchMode ? ` (Search Results)` : ' (Recent Bets)'}
//           </Typography>
//         </Box>

//         {/* No bets found */}
//         {!searchMode && !isLoading && bets.length === 0 && (
//           <Alert severity="warning" sx={{ mt: 3 }}>
//             No betting data available. This could be due to no recent activity or database connectivity issues.
//           </Alert>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Games;

// // // src/pages/Games/Games.jsx
// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container,
//   TextField,
//   InputAdornment,
//   Button,
//   Fade,
//   Collapse,
//   alpha,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Select,
//   LinearProgress,
//   Avatar
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   SportsEsports as SportsEsportsIcon,
//   Casino as CasinoIcon,
//   People as PeopleIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
//   Search as SearchIcon,
//   Clear as ClearIcon,
//   Phone as PhoneIcon,
//   EmojiEvents as EmojiEventsIcon,
//   FilterList as FilterListIcon,
//   AccountBalance as AccountBalanceIcon,
//   Analytics as AnalyticsIcon,
//   CheckCircle as CheckCircleIcon,
//   Error as ErrorIcon,
//   HourglassEmpty as HourglassEmptyIcon,
//   SportsSoccer as SportsSoccerIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetRecentBetsQuery, useLazyGetRecentBetsQuery } from '../../store/api/dashboardApi';

// const Games = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   // Search and filter state
//   const [searchInput, setSearchInput] = React.useState('');
//   const [searchQuery, setSearchQuery] = React.useState('');
//   const [searchMode, setSearchMode] = React.useState(false);
//   const [serviceCodeFilter, setServiceCodeFilter] = React.useState('');
//   const [showFilters, setShowFilters] = React.useState(false);
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   // Main query - skipped during search mode
//   const { 
//     data: betsResponse, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetRecentBetsQuery(allTimeFilters, {
//     skip: searchMode,
//   });

//   // Lazy search query
//   const [
//     searchBets, 
//     { 
//       data: searchResults, 
//       isLoading: isSearching, 
//       error: searchError 
//     }
//   ] = useLazyGetRecentBetsQuery();

//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Handle search input change with debounce
//   const searchTimeoutRef = React.useRef();

//   const handleSearchInputChange = (event) => {
//     const value = event.target.value;
//     setSearchInput(value);
    
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
    
//     searchTimeoutRef.current = setTimeout(() => {
//       const trimmedQuery = value.trim();
//       if (trimmedQuery) {
//         performSearch(trimmedQuery, serviceCodeFilter);
//       } else {
//         clearSearch();
//       }
//     }, 500); // 500ms debounce
//   };

//   const handleServiceCodeChange = (event) => {
//     const value = event.target.value;
//     setServiceCodeFilter(value);
    
//     if (searchInput.trim() || value) {
//       performSearch(searchInput.trim(), value);
//     }
//   };

//   const performSearch = (query, serviceCode) => {
//     const searchParams = {};
    
//     if (query) {
//       searchParams.search = query;
//     }
    
//     if (serviceCode) {
//       searchParams.service_code = serviceCode;
//     }
    
//     setSearchQuery(query);
//     setSearchMode(true);
//     searchBets(searchParams);
//   };

//   const clearSearch = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setServiceCodeFilter('');
//     setSearchMode(false);
//     setShowFilters(false);
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
//   };

//   // Get display data based on search mode
//   const getDisplayData = () => {
//     if (searchMode) {
//       return searchResults || {
//         data: [],
//         pagination: { total: 0, currentPage: 1, totalPages: 1 },
//         filters: {}
//       };
//     }
//     return betsResponse;
//   };

//   const displayData = getDisplayData();
//   const bets = displayData?.data || [];
//   const pagination = displayData?.pagination || {};

//   const rows = React.useMemo(() => {
//     return bets.map((bet, index) => ({
//       id: bet.betId || bet.id || `bet-${index}`,
//       ...bet,
//       betDate: bet.betDate ? new Date(bet.betDate) : null,
//       createdAt: bet.createdAt ? new Date(bet.createdAt) : null,
//     }));
//   }, [bets]);

//   // Game-specific helper functions
//   const getGameIcon = (serviceCode) => {
//     const code = serviceCode?.toLowerCase();
//     if (code?.includes('sport') || code?.includes('soccer') || code?.includes('football')) {
//       return <SportsSoccerIcon sx={{ fontSize: 16 }} />;
//     }
//     if (code?.includes('casino') || code?.includes('slot')) {
//       return <CasinoIcon sx={{ fontSize: 16 }} />;
//     }
//     return <SportsEsportsIcon sx={{ fontSize: 16 }} />;
//   };

//   const getGameColor = (serviceCode) => {
//     const code = serviceCode?.toLowerCase();
//     if (code?.includes('sport')) return 'success';
//     if (code?.includes('casino')) return 'error';
//     if (code?.includes('lottery')) return 'warning';
//     return 'primary';
//   };

//   const getBetStatusColor = (status) => {
//     const statusLower = status?.toLowerCase();
//     switch (statusLower) {
//       case 'won':
//       case 'win':
//       case 'success':
//         return 'success';
//       case 'lost':
//       case 'lose':
//       case 'failed':
//         return 'error';
//       case 'pending':
//       case 'active':
//         return 'warning';
//       case 'cancelled':
//       case 'void':
//         return 'default';
//       default:
//         return 'info';
//     }
//   };

//   const getBetStatusIcon = (status) => {
//     const statusLower = status?.toLowerCase();
//     switch (statusLower) {
//       case 'won':
//       case 'win':
//       case 'success':
//         return <CheckCircleIcon sx={{ fontSize: 16 }} />;
//       case 'pending':
//       case 'active':
//         return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
//       case 'lost':
//       case 'lose':
//       case 'failed':
//         return <ErrorIcon sx={{ fontSize: 16 }} />;
//       default:
//         return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
//     }
//   };

//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' },
//                 fontWeight: searchMode ? 'bold' : 'normal',
//                 color: searchMode ? 'primary.main' : 'inherit'
//               }}
//             >
//               {params.value}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'betAmount', 
//         headerName: 'Bet Amount', 
//         width: isMobile ? 100 : 120, 
//         type: 'number',
//         minWidth: 90,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             <AccountBalanceIcon sx={{ fontSize: 16, color: 'info.main' }} />
//             <Typography 
//               variant="body2" 
//               color="info.main"
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'potentialWin', 
//         headerName: isMobile ? 'Win' : 'Potential Win', 
//         width: isMobile ? 90 : 130, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             <EmojiEventsIcon sx={{ fontSize: 16, color: 'success.main' }} />
//             <Typography 
//               variant="body2" 
//               color="success.main"
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'serviceCode', 
//         headerName: 'Game', 
//         width: isMobile ? 100 : 140, 
//         minWidth: 90,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             {getGameIcon(params.value)}
//             <Chip 
//               label={params.value || 'N/A'}
//               color={getGameColor(params.value)}
//               size="small"
//               variant="outlined"
//               sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//             />
//           </Box>
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { 
//           field: 'betStatus', 
//           headerName: 'Status', 
//           width: 120,
//           renderCell: (params) => (
//             <Chip 
//               label={params.value || 'PENDING'}
//               color={getBetStatusColor(params.value)}
//               size="small"
//               variant="outlined"
//               icon={getBetStatusIcon(params.value)}
//               sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//             />
//           )
//         },
//         { 
//           field: 'odds', 
//           headerName: 'Odds', 
//           width: 80, 
//           type: 'number',
//           valueFormatter: (value) => value ? `${value}x` : 'N/A'
//         }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { 
//           field: 'betDate', 
//           headerName: 'Bet Date', 
//           width: 120, 
//           type: 'dateTime',
//           valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
//         },
//         { 
//           field: 'gameDetails', 
//           headerName: 'Game Details', 
//           width: 160,
//           renderCell: (params) => (
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontSize: '0.8rem',
//                 color: 'text.secondary',
//                 overflow: 'hidden',
//                 textOverflow: 'ellipsis'
//               }}
//             >
//               {params.value || 'N/A'}
//             </Typography>
//           )
//         }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet, searchMode]);

//   // Get unique service codes for filter
//   const serviceCodes = React.useMemo(() => {
//     const codes = new Set();
//     bets.forEach(bet => {
//       if (bet.serviceCode) {
//         codes.add(bet.serviceCode);
//       }
//     });
//     return Array.from(codes).sort();
//   }, [bets]);

//   // Calculate game statistics
//   const gameStats = React.useMemo(() => {
//     const stats = {
//       totalBets: bets.length,
//       totalBetAmount: 0,
//       totalPotentialWin: 0,
//       uniquePlayers: new Set(),
//       gameBreakdown: {},
//       statusBreakdown: {}
//     };

//     bets.forEach(bet => {
//       stats.totalBetAmount += bet.betAmount || 0;
//       stats.totalPotentialWin += bet.potentialWin || 0;
//       if (bet.phone) stats.uniquePlayers.add(bet.phone);
      
//       const game = bet.serviceCode || 'Unknown';
//       stats.gameBreakdown[game] = (stats.gameBreakdown[game] || 0) + 1;
      
//       const status = bet.betStatus || 'PENDING';
//       stats.statusBreakdown[status] = (stats.statusBreakdown[status] || 0) + 1;
//     });

//     stats.uniquePlayers = stats.uniquePlayers.size;
//     stats.avgBetAmount = stats.totalBets > 0 ? stats.totalBetAmount / stats.totalBets : 0;

//     return stats;
//   }, [bets]);

//   const summaryCards = [
//     { 
//       title: 'Total Bets', 
//       value: gameStats.totalBets.toLocaleString(), 
//       color: 'primary.main', 
//       icon: <SportsEsportsIcon />, 
//       bgColor: theme.palette.primary.light 
//     },
//     { 
//       title: 'Total Bet Amount', 
//       value: `$${gameStats.totalBetAmount.toLocaleString()}`, 
//       color: 'info.main', 
//       icon: <AccountBalanceIcon />, 
//       bgColor: theme.palette.info.light 
//     },
//     { 
//       title: 'Potential Winnings', 
//       value: `$${gameStats.totalPotentialWin.toLocaleString()}`, 
//       color: 'success.main', 
//       icon: <EmojiEventsIcon />, 
//       bgColor: theme.palette.success.light 
//     },
//     { 
//       title: 'Active Players', 
//       value: gameStats.uniquePlayers.toLocaleString(), 
//       color: 'warning.main', 
//       icon: <PeopleIcon />, 
//       bgColor: theme.palette.warning.light 
//     }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading && !searchMode) {
//     return (
//       <Container maxWidth="xl">
//         <Box sx={{ py: 2 }}>
//           <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
//           <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
//           <LoadingSkeleton />
//         </Box>
//       </Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'}
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Games & Bets {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               {searchMode ? `Search results for: ${searchQuery || 'filtered data'}` : 'Recent betting activity and game performance analysis'}
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton 
//               onClick={() => refetch()} 
//               disabled={isFetching || isSearching} 
//               color="primary" 
//               sx={{ 
//                 bgcolor: 'primary.light', 
//                 '&:hover': { bgcolor: 'primary.main', color: 'white' } 
//               }}
//             >
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Summary Cards */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ 
//                 height: '100%', 
//                 background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
//                 border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
//                 transition: 'transform 0.2s, box-shadow 0.2s', 
//                 '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
//               }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
//                       {card.icon}
//                     </Box>
//                     <Typography 
//                       component="div" 
//                       sx={{ 
//                         color: card.color, 
//                         fontWeight: 'bold', 
//                         fontSize: { xs: '1rem', md: '1.25rem' } 
//                       }}
//                     >
//                       {card.value}
//                     </Typography>
//                   </Stack>
//                   <Typography 
//                     variant="body2" 
//                     color="text.secondary" 
//                     sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
//                   >
//                     {card.title}
//                   </Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Game Breakdown */}
//         {Object.keys(gameStats.gameBreakdown).length > 0 && (
//           <Card sx={{ mb: 3 }}>
//             <CardContent>
//               <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <AnalyticsIcon />
//                 Game Breakdown
//               </Typography>
//               <Grid container spacing={2}>
//                 {Object.entries(gameStats.gameBreakdown).map(([game, count]) => {
//                   const percentage = (count / gameStats.totalBets) * 100;
//                   return (
//                     <Grid item xs={12} sm={6} md={4} key={game}>
//                       <Box sx={{ 
//                         p: 2, 
//                         border: '1px solid', 
//                         borderColor: 'divider', 
//                         borderRadius: 1,
//                         bgcolor: alpha(theme.palette[getGameColor(game)]?.main || theme.palette.primary.main, 0.05)
//                       }}>
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
//                           {getGameIcon(game)}
//                           <Typography variant="subtitle2">{game}</Typography>
//                         </Box>
//                         <Typography variant="h6" color={`${getGameColor(game)}.main`}>
//                           {count} bets
//                         </Typography>
//                         <LinearProgress 
//                           variant="determinate" 
//                           value={percentage} 
//                           color={getGameColor(game)}
//                           sx={{ mt: 1, height: 6, borderRadius: 3 }}
//                         />
//                         <Typography variant="caption" color="text.secondary">
//                           {percentage.toFixed(1)}% of total
//                         </Typography>
//                       </Box>
//                     </Grid>
//                   );
//                 })}
//               </Grid>
//             </CardContent>
//           </Card>
//         )}

//         {/* Search and Filter Section */}
//         <Card sx={{ 
//           mb: 3, 
//           background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
//           border: '1px solid',
//           borderColor: 'divider',
//           transition: 'transform 0.2s, box-shadow 0.2s', 
//           '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
//         }}>
//           <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//             <Stack spacing={2}>
//               {/* Search Bar */}
//               <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
//                 <TextField
//                   fullWidth
//                   placeholder="Search by phone number or game details..."
//                   value={searchInput}
//                   onChange={handleSearchInputChange}
//                   InputProps={{
//                     startAdornment: (
//                       <InputAdornment position="start">
//                         <SearchIcon color="action" />
//                       </InputAdornment>
//                     ),
//                     endAdornment: searchInput && (
//                       <InputAdornment position="end">
//                         <IconButton onClick={clearSearch} size="small">
//                           <ClearIcon />
//                         </IconButton>
//                       </InputAdornment>
//                     )
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       backgroundColor: theme.palette.background.paper,
//                     }
//                   }}
//                 />
                
//                 <Button
//                   variant="outlined"
//                   startIcon={<FilterListIcon />}
//                   onClick={() => setShowFilters(!showFilters)}
//                   sx={{ minWidth: 'fit-content' }}
//                 >
//                   Filters
//                 </Button>
                
//                 {isSearching && (
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
//                     <CircularProgress size={20} />
//                     <Typography variant="body2" color="text.secondary">Searching...</Typography>
//                   </Box>
//                 )}
//               </Stack>
              
//               {/* Filters */}
//               <Collapse in={showFilters}>
//                 <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pt: 2 }}>
//                   <FormControl sx={{ minWidth: 200 }}>
//                     <InputLabel>Game Type</InputLabel>
//                     <Select
//                       value={serviceCodeFilter}
//                       label="Game Type"
//                       onChange={handleServiceCodeChange}
//                     >
//                       <MenuItem value="">All Games</MenuItem>
//                       {serviceCodes.map((code) => (
//                         <MenuItem key={code} value={code}>
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                             {getGameIcon(code)}
//                             {code}
//                           </Box>
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Stack>
//               </Collapse>
//             </Stack>
//           </CardContent>
//         </Card>

//         {/* Search Results Alert */}
//         {searchMode && bets.length === 0 && (
//           <Fade in>
//             <Alert severity="info" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 No bets found for the current search criteria.
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Bets
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {searchMode && bets.length > 0 && (
//           <Fade in>
//             <Alert severity="success" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 ✅ Found {bets.length} bet{bets.length !== 1 ? 's' : ''}
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Bets
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {/* Error Handling */}
//         {(error || searchError) && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             Error loading bet data: {error?.message || searchError?.message || 'Unknown error'}
//           </Alert>
//         )}

//         {/* Data Grid Section */}
//         <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//           <DataGrid
//             rows={rows}
//             columns={getColumns()}
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//             rowCount={pagination.total || rows.length}
//             loading={isLoading || isFetching || isSearching}
//             disableRowSelectionOnClick
//             sx={{
//               '& .MuiDataGrid-root': { border: 'none' },
//               '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider' },
//               '& .MuiDataGrid-columnHeaders': { 
//                 backgroundColor: alpha(theme.palette.primary.light, 0.1), 
//                 borderBottom: '2px solid', 
//                 borderBottomColor: 'divider' 
//               },
//               '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//               '& .MuiDataGrid-row:nth-of-type(even)': { 
//                 backgroundColor: alpha(theme.palette.action.hover, 0.5) 
//               },
//               ...(isMobile && {
//                 '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                 '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//               }),
//             }}
//             getRowId={(row) => row.id}
//             density={isMobile ? 'compact' : 'standard'}
//           />
//         </Paper>

//         {/* Pagination Info */}
//         <Box sx={{ 
//           mt: 2, 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: 'center', 
//           flexDirection: { xs: 'column', md: 'row' }, 
//           gap: 1 
//         }}>
//           <Typography variant="caption" color="text.secondary">
//             {searchMode && <Chip label="Filtered" size="small" sx={{ mr: 1 }} />}
//             Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
//           </Typography>
//           <Typography variant="caption" color="text.secondary">
//             Showing {rows.length} bet{rows.length !== 1 ? 's' : ''} of {pagination.total || rows.length} total
//             {searchMode ? ` (Search Results)` : ' (Recent Bets)'}
//           </Typography>
//         </Box>

//         {/* No bets found */}
//         {!searchMode && !isLoading && bets.length === 0 && (
//           <Alert severity="warning" sx={{ mt: 3 }}>
//             No betting data available. This could be due to no recent activity or database connectivity issues.
//           </Alert>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Games;
// import React from 'react';
// import { Box, Typography, Paper } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';

// const Games = () => {
//   const [loading, setLoading] = React.useState(false);
//   const [rows, setRows] = React.useState([]);
//   const [rowCount, setRowCount] = React.useState(0);
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: 25,
//   });

//   const columns = [
//     { field: 'id', headerName: 'ID', width: 90 },
//     { field: 'name', headerName: 'Game Name', width: 200 },
//     { field: 'category', headerName: 'Category', width: 150 },
//     { field: 'totalPlays', headerName: 'Total Plays', width: 120, type: 'number' },
//     { field: 'totalWins', headerName: 'Total Wins', width: 120, type: 'number' },
//     { field: 'status', headerName: 'Status', width: 120 },
//   ];

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//         Games
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//         Manage available games and their statistics
//       </Typography>

//       <Paper sx={{ height: 600, width: '100%' }}>
//         <DataGrid
//           rows={rows}
//           columns={columns}
//           paginationModel={paginationModel}
//           onPaginationModelChange={setPaginationModel}
//           pageSizeOptions={[25, 50, 100]}
//           rowCount={rowCount}
//           loading={loading}
//           paginationMode="server"
//           sortingMode="server"
//           filterMode="server"
//           disableRowSelectionOnClick
//         />
//       </Paper>
//     </Box>
//   );
// };

// export default Games;